(() => {
  const modal = document.getElementById('SearchModal');
  const mobileBar = document.getElementById('SearchMobileBar');
  const desktopSearchMql = window.matchMedia('(min-width: 640px)');

  const isDesktopSearch = () => desktopSearchMql.matches;

  if (!modal && !mobileBar) return;

  const headerSectionSelector = '.header-section';

  /**
   * Distance from viewport top to where the header block starts (matches sticky header visually).
   * Using announcement height alone breaks after scroll: the bar scrolls away but the var stayed fixed, leaving a gap.
   */
  const setSearchModalTop = () => {
    const headerSection = document.querySelector(headerSectionSelector);
    let px = '0px';
    if (headerSection && !headerSection.hasAttribute('hidden')) {
      const cs = window.getComputedStyle(headerSection);
      if (cs.display !== 'none' && cs.visibility !== 'hidden') {
        const top = Math.max(0, Math.round(headerSection.getBoundingClientRect().top));
        if (top > 0) px = `${top}px`;
      }
    }
    document.documentElement.style.setProperty('--search-modal-top', px);
  };

  let topRaf = 0;
  const scheduleSearchModalTop = () => {
    if (topRaf) return;
    topRaf = requestAnimationFrame(() => {
      topRaf = 0;
      setSearchModalTop();
    });
  };

  setSearchModalTop();
  window.addEventListener('resize', scheduleSearchModalTop);
  window.addEventListener('scroll', scheduleSearchModalTop, { passive: true });

  const headerSectionEl = document.querySelector(headerSectionSelector);
  if (headerSectionEl && typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(scheduleSearchModalTop).observe(headerSectionEl);
  }

  document.addEventListener('shopify:section:load', scheduleSearchModalTop);
  document.addEventListener('shopify:section:unload', scheduleSearchModalTop);

  const form = modal?.querySelector('form[is="predictive-search"]') || modal?.querySelector('form.search-modal__form');
  const modalSearchInput = modal?.querySelector('input.search-modal__input[type="search"]');
  const mobileBarInput = document.getElementById('SearchMobileBarInput');

  const drawerEl = () => document.getElementById('SearchDrawer') || document.querySelector('search-drawer');

  const closeDrawer = () => {
    const sd = drawerEl();
    if (sd && typeof sd.hide === 'function') sd.hide();
  };

  const setMobileBarHeightVar = () => {
    if (!mobileBar || isDesktopSearch()) {
      document.documentElement.style.removeProperty('--search-mobile-bar-height');
      return;
    }
    const h = Math.round(mobileBar.getBoundingClientRect().height) - 1;
    document.documentElement.style.setProperty('--search-mobile-bar-height', `${h}px`);
  };

  setMobileBarHeightVar();
  window.addEventListener('resize', () => requestAnimationFrame(setMobileBarHeightVar));
  if (mobileBar && typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(() => requestAnimationFrame(setMobileBarHeightVar)).observe(mobileBar);
  }
  desktopSearchMql.addEventListener('change', () => requestAnimationFrame(setMobileBarHeightVar));

  const openModal = (options = {}) => {
    if (!modal) return;
    const keepMobileBar = !isDesktopSearch() && Boolean(options.keepMobileBar);
    if (keepMobileBar) {
      document.body.classList.add('search-modal-open--keep-mobile-bar');
      modal.classList.add('search-modal--below-mobile-bar');
    } else {
      document.body.classList.remove('search-modal-open--keep-mobile-bar');
      modal.classList.remove('search-modal--below-mobile-bar');
    }

    setSearchModalTop();
    closeDrawer();
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('search-modal-open');
    requestAnimationFrame(() => {
      modal.classList.add('search-modal--visible');
    });
    const input = modal.querySelector('input[type="search"]');
    if (!keepMobileBar && input) {
      // Focus synchronously, within the click gesture, so iOS/Android open the
      // soft keyboard. A deferred focus (setTimeout) loses the gesture context
      // and the mobile keyboard never shows — desktop got a cursor, mobile didn't.
      input.focus({ preventScroll: true });
    }
  };

  const closeModal = () => {
    if (!modal) return;
    modal.classList.remove('search-modal--visible');
    document.body.classList.remove('search-modal-open');
    document.body.classList.remove('search-modal-open--keep-mobile-bar');
    modal.classList.remove('search-modal--below-mobile-bar');

    if (form && typeof form.clear === 'function') {
      form.clear();
    } else if (form) {
      form.reset();
    }

    if (mobileBarInput) {
      mobileBarInput.value = '';
    }

    window.setTimeout(() => {
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
    }, 300);
  };

  document.addEventListener(
    'click',
    (e) => {
      const trigger = e.target.closest(
        'a.search-drawer-button[aria-controls="SearchDrawer"], button.search-drawer-button[aria-controls="SearchDrawer"]'
      );
      if (!trigger) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      openModal({});
    },
    true
  );

  /** Mobile: sticky bar + modal below it; sync query into the predictive form on every keystroke */
  if (mobileBarInput && modal && modalSearchInput && form) {
    mobileBarInput.addEventListener('input', () => {
      if (isDesktopSearch()) return;
      const q = mobileBarInput.value.trim();
      if (!q) {
        if (
          modal.classList.contains('search-modal--visible') &&
          document.body.classList.contains('search-modal-open--keep-mobile-bar')
        ) {
          closeModal();
        }
        return;
      }
      const keepOpen = document.body.classList.contains('search-modal-open--keep-mobile-bar');
      if (!keepOpen) {
        openModal({ keepMobileBar: true });
      }
      modalSearchInput.value = mobileBarInput.value;
      modalSearchInput.dispatchEvent(new Event('input', { bubbles: true }));
    });
  }

  /** Mobile (<640px): empty modal field closes overlay + predictive inner (header-open path) */
  if (modalSearchInput && modal) {
    modalSearchInput.addEventListener('input', () => {
      if (isDesktopSearch()) return;
      if (modal.hidden || !modal.classList.contains('search-modal--visible')) return;
      if (modalSearchInput.value.trim() !== '') return;
      closeModal();
    });
  }

  /**
   * Dismiss on scrim / outside results. The panel uses pointer-events: none with auto only on
   * .search__content — touches must not fall through to the scrim (mobile).
   */
  modal?.addEventListener('click', (e) => {
    if (modal.hidden || !modal.classList.contains('search-modal--visible')) return;

    if (e.target.closest('[data-search-modal-close]')) {
      e.preventDefault();
      closeModal();
      return;
    }

    if (e.target.closest('.search-modal__field-wrap')) return;
    if (e.target.closest('.search-modal__panel')) return;
    if (e.target.closest('[data-search-modal-interactive-pane]')) return;

    closeModal();
  });

  document.addEventListener('keyup', (e) => {
    if (e.code !== 'Escape') return;
    if (!modal || modal.hidden || !modal.classList.contains('search-modal--visible')) return;
    closeModal();
  });
})();
